import { intersection } from 'fe-core/util/intersection';
import { uniq } from 'lodash';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

import { getCurrentLocationId } from 'selectors/session';

import { DEFAULT_ROLE_COLOR } from 'features/scheduleBuilder/constants';
import {
  CONSECUTIVE_SHIFTS_MAX,
  DIALOGS,
  DRAWERS,
  EMPLOYEE_VIEW_FILTERS,
  MODALS,
  SLICE_NAME,
  SORT_BY_WEEK_OPTIONS,
  VIEW_TYPES,
} from 'features/scheduleBuilder/ScheduleBuilderView/constants';
import {
  getUser,
  sortByAttribute,
} from 'features/scheduleBuilder/ScheduleBuilderView/selectors/helpers';
import {
  selectDecoratedShifts,
  selectShiftIdJobIdMap,
  selectShiftsByRoleId,
} from 'features/scheduleBuilder/ScheduleBuilderView/selectors/shared';
import { containsShiftsInCurrentLocation } from 'features/scheduleBuilder/ScheduleBuilderView/utils/containsShiftsInCurrentLocation';
import { selectAllConflicts } from 'features/scheduleBuilder/slices/conflicts/slice';
import { selectCustomJobPositionsIds as selectCustomJobPositionsIdsFromSlice } from 'features/scheduleBuilder/slices/customJobPositions/slice';
import { selectAllDepartments } from 'features/scheduleBuilder/slices/departments/slice';
import { selectSliceAttribute } from 'features/scheduleBuilder/slices/helpers';
import { selectAllJobPositions } from 'features/scheduleBuilder/slices/jobPositions/slice';
import {
  selectAllJobs,
  selectJobsWhereAttribute,
} from 'features/scheduleBuilder/slices/jobs/slice';
import { selectLocationAttribute } from 'features/scheduleBuilder/slices/locations/slice';
import { selectRoleById } from 'features/scheduleBuilder/slices/roles';
import { selectAllScheduleAvailabilities } from 'features/scheduleBuilder/slices/scheduleAvailabilities/slice';
import { shiftSortCompare } from 'features/scheduleBuilder/slices/shifts/helpers';
import {
  selectAllShifts,
  selectShiftAttribute,
  selectShiftById,
  selectShiftRelationshipEntity,
  selectShiftWhereAttribute,
} from 'features/scheduleBuilder/slices/shifts/slice';
import { selectAllTimeOffs } from 'features/scheduleBuilder/slices/timeOffs/slice';
import {
  selectJobIdsForUser,
  selectUserIdJobIdMap,
  selectUserIdUserNameMap,
} from 'features/scheduleBuilder/slices/users/slice';
import {
  generateNextNWeeks,
  getEntityCurrentDate,
  getScheduleAvailabilitiesIds,
  isDateInRange,
  isShiftWithinRange,
} from 'features/scheduleBuilder/util';

import {
  compareDateTime,
  dateFromTime,
  datesInRange,
  df,
  startOfCurrentWeek,
} from 'util/dateTime';
import { toI18n } from 'util/i18n';

import sortEmployeeInDepartmentByName from '../utils/sortEmployeeInDepartmentByName';
import timeOffSortCompare from '../utils/timeOffSortCompare';

const SHIFT_FORM_I18N_PATH = 'schedule_builder.react_page.shift_form';

const selectSlice = state => state.get(SLICE_NAME) || {};

const getCurrentLocationIdForSchedule = state =>
  String(
    getCurrentLocationId(state) || state.getIn(['session'])?.currentLocation?.id
  );

export const selectIsJumpstartClosed = createSelector(
  selectSlice,
  slice => slice.isJumpstartClosed
);

export const selectIsAvailabilitiesVisible = createSelector(
  selectSlice,
  slice => slice.isAvailabilitiesVisible
);

export const selectIsTimeOffsVisible = createSelector(
  selectSlice,
  slice => slice.isTimeOffsVisible
);

export const selectIsShiftsAtThisLocationOnly = createSelector(
  selectSlice,
  slice => slice.isShiftsAtThisLocationOnly
);

export const selectNotificationMessage = createSelector(
  selectSlice,
  slice => slice.notificationCenterMessage
);

export const selectNotificationType = createSelector(
  selectSlice,
  slice => slice.notificationCenterType
);

export const selectNotificationIsAI = createSelector(
  selectSlice,
  slice => slice.notificationCenterMessageAI
);

export const selectDepartmentId = createSelector(
  selectSlice,
  slice => slice.departmentId
);

export const selectIsScheduleUsersOnly = createSelector(
  selectSlice,
  slice => slice.employeeViewFilter === EMPLOYEE_VIEW_FILTERS.SCHEDULED
);

export const selectCopyPasteShift = createSelector(
  selectSlice,
  slice => slice.copyPasteShift
);

export const selectIsCopyShiftModeEnabled = createSelector(
  selectCopyPasteShift,
  copiedShift => !!copiedShift
);

export const selectConflictsEnabled = createSelector(
  selectSlice,
  slice => slice.conflictsEnabled
);

export const selectCustomJobIds = createSelector(
  selectSlice,
  slice => slice.customJobIds
);

export const selectSortByDay = createSelector(
  selectSlice,
  slice => slice.sortByDay
);

export const selectCopyWeekModalIsOpen = createSelector(
  selectSlice,
  slice => slice.copyWeekModalIsOpen
);

export const selectSchedulingInsightsModalIsOpen = createSelector(
  selectSlice,
  slice => slice.schedulingInsightsModalIsOpen
);

export const selectSchedulingInsightsModalCloseIconClicked = createSelector(
  selectSlice,
  slice => slice.schedulingInsightsModalCloseIconClicked
);

export const selectCurrentDateRange = createSelector(
  selectSlice,
  slice => slice.currentDateRange
);

export const selectCurrentDateRangeStart = createSelector(
  selectCurrentDateRange,
  currentDateRange => currentDateRange?.start
);

export const selectCurrentDateRangeEnd = createSelector(
  selectCurrentDateRange,
  currentDateRange => currentDateRange?.end
);

export const selectViewType = createSelector(
  selectSlice,
  slice => slice.viewType
);

export const selectRangeType = createSelector(
  selectSlice,
  slice => slice.rangeType
);

export const selectIsPastWeek = createSelector(
  selectCurrentDateRangeStart,
  startDate =>
    compareDateTime(startOfCurrentWeek(df('parsable_reversed')), startDate) > 0
);

export const selectNextNWeeks = createCachedSelector(
  state => selectCurrentDateRange(state)?.end,
  (_, props) => props.numberOfWeeks,
  (endDate, numberOfWeeks) =>
    generateNextNWeeks({ start: endDate, numberOfWeeks })
)((_, props) => `selectNextNWeeks-${props.numberOfWeeks}`);

export const selectShiftColor = createCachedSelector(
  (state, { id }) =>
    selectShiftAttribute({
      state,
      id,
      attribute: 'color',
    }),
  (state, { id }) => {
    const roleId = selectShiftRelationshipEntity({
      state,
      id,
      relationship: 'role',
    })?.id;
    return roleId ? selectRoleById(state, roleId)?.color : DEFAULT_ROLE_COLOR;
  },
  (shiftColor, defaultRoleColor) => shiftColor || defaultRoleColor
)((_, { id }) => `selectShiftColor-${id}`);

export const selectSearchTerm = createSelector(
  selectSlice,
  slice => slice.searchTerm
);

export const selectDrawerUxContext = createSelector(
  selectSlice,
  slice => slice.drawerUxContext
);

export const selectJumpstartDrawerIsShown = createSelector(
  selectSlice,
  slice => slice.activeDrawer === DRAWERS.jumpstart_drawer
);

export const selectPublishDrawerShown = createSelector(
  selectSlice,
  slice => slice.activeDrawer === DRAWERS.publish_drawer
);

export const selectPrintScheduleDrawerShown = createSelector(
  selectSlice,
  slice => slice.activeDrawer === DRAWERS.print_schedule_drawer
);

export const selectTemplatesDrawerOpen = createSelector(
  selectSlice,
  slice => slice.activeDrawer === DRAWERS.templates_drawer
);

export const selectCopyWeekDrawerShown = createSelector(
  selectSlice,
  slice => slice.activeDrawer === DRAWERS.copy_week_drawer
);

export const selectPendingJobLaborTotalsIds = createSelector(
  selectSlice,
  slice => slice.pendingJobLaborTotalsIds || []
);

export const selectCompanyShifts = createCachedSelector(
  selectSlice,
  slice => slice.companyLaborTotals || []
)((_, props) => `selectCompanyShifts-${props.date}`);

export const selectCompanyLaborHoursByJobId = createCachedSelector(
  selectCompanyShifts,
  (_, props) => props.jobId,
  selectCurrentDateRangeStart,
  selectCurrentDateRangeEnd,
  (companyLaborShifts, jobId, startDate, endDate) => {
    const userCompanyShifts =
      companyLaborShifts.find(labor => String(labor.job_id) === jobId)
        ?.shifts || [];

    if (userCompanyShifts.length === 0) return 0;

    const userCompanyShiftsWithinDateRange = userCompanyShifts.filter(shift => {
      const shiftStartDate = shift.start_at.split('T')[0];
      const shiftStart = Date.parse(shiftStartDate);
      const rangeStart = Date.parse(startDate);
      const rangeEnd = Date.parse(endDate);

      return shiftStart >= rangeStart && shiftStart <= rangeEnd;
    });

    return userCompanyShiftsWithinDateRange.reduce(
      (total, shift) => total + shift.hours,
      0
    );
  }
)((_, props) => `selectCompanyLaborHoursByJobId-${props.date}`);

export const selectHasPendingData = createSelector(
  selectSlice,
  slice => slice.hasPendingData
);

// Note: Cannot be used in other selectors since this data is being mutated
// when we build row data (see addRowData)
export const selectShiftsByJobAndDate = createSelector(
  selectDecoratedShifts,
  shifts =>
    shifts.reduce((memo, shift) => {
      const { id: ownerId, type: ownerType } = shift.owner;

      if (ownerType !== 'job') return memo;

      const date = shift.decoration.date;

      memo[ownerId] ||= {};
      memo[ownerId][date] ||= [];
      memo[ownerId][date].push(shift);
      return memo;
    }, {})
);

export const selectAllEmployeeIds = state =>
  selectSliceAttribute({
    state,
    attribute: 'jobs',
    sliceName: 'schedule',
  })?.map(job => job.id) || [];

export const selectJobsForCurrentLocation = createSelector(
  getCurrentLocationIdForSchedule,
  selectAllJobs,
  (currentLocationId, jobs) =>
    jobs?.filter(job => job.id && job.location?.id === currentLocationId) || []
);

export const selectJobIdArchivedAtMap = createSelector(
  selectJobsForCurrentLocation,
  jobs =>
    jobs?.reduce((memo, job) => {
      if (job?.archivedAt) {
        memo[job.id] = job.archivedAt;
      }

      return memo;
    }, {})
);

export const selectJobIdUserIdMap = createSelector(
  selectJobsForCurrentLocation,
  jobs =>
    jobs?.reduce((memo, job) => {
      if (job.user?.id) {
        memo[job.id] = job.user.id;
      }
      return memo;
    }, {})
);

export const selectJobIdWageRateMap = createSelector(selectAllJobs, jobs =>
  jobs?.reduce((memo, job) => {
    if (job?.wageRate) {
      memo[job.id] = job.wageRate;
    }
    return memo;
  }, {})
);

export const selectJobIdLocationIdMap = createSelector(selectAllJobs, jobs =>
  jobs?.reduce((memo, job) => {
    if (job?.location?.id) {
      memo[job.id] = job?.location?.id;
    }
    return memo;
  }, {})
);

export const selectJobIdsForCurrentLocation = createSelector(
  state =>
    selectJobsForCurrentLocation(state)
      .map(job => job.id)
      .join(','),
  ids => (ids.length ? ids.split(',') : [])
);

export const selectDatesInCurrentRange = createSelector(
  selectCurrentDateRangeStart,
  selectCurrentDateRangeEnd,
  (startDate, endDate) => datesInRange(startDate, endDate)
);
export const selectJobIdsForUserScheduled = createCachedSelector(
  selectDecoratedShifts,
  selectDatesInCurrentRange,
  selectJobIdsForUser,
  (shifts, currentDateRange, jobIds) =>
    uniq(
      shifts
        .filter(s => currentDateRange.includes(s.decoration.date))
        .map(s => s.owner.id)
        .filter(value => jobIds.includes(value))
    )
)((_, props) => `selectLocationIdsForUserScheduled-${props.jobId}`);

const selectShiftsByDateAndJob = createSelector(
  selectDecoratedShifts,
  selectJobIdUserIdMap,
  (shifts, jobIdUserIdMap) =>
    shifts
      .filter(shift => Object.keys(jobIdUserIdMap).includes(shift?.owner?.id))
      .reduce((memo, shift) => {
        const {
          decoration: { date },
          owner: { id, type },
        } = shift;

        if (type !== 'job') return memo;

        memo[date] ||= {};
        memo[date][id] = shift;
        return memo;
      }, {})
);

export const selectScheduledEmployeeIds = createSelector(
  selectShiftsByDateAndJob,
  selectDatesInCurrentRange,
  (shifts, datesInCurrentRange) => {
    const jobIds = [];

    datesInCurrentRange.forEach(date => {
      if (shifts[date]) {
        jobIds.push(Object.keys(shifts[date]));
      }
    });

    const jobIdsUnique = jobIds.flat();

    return jobIdsUnique.filter(
      (value, index) => jobIdsUnique.indexOf(value) === index
    );
  }
);

export const selectCustomJobPositionIds = createSelector(
  state => {
    // TODO: Remove this with custom job positions consumption
    const customJobPositionIds =
      selectCustomJobIds(state).length > 0
        ? selectCustomJobIds(state)
        : selectCustomJobPositionsIdsFromSlice(state);

    return customJobPositionIds.join(',');
  },
  ids => (ids.length ? ids.split(',') : [])
);

export const selectJobPositionIds = createSelector(
  state =>
    selectAllJobPositions(state)
      .filter(jobPosition =>
        selectJobIdsForCurrentLocation(state).includes(jobPosition.id)
      )
      .sort((a, b) =>
        sortByAttribute(
          a?.position || a?.attributes?.position,
          b?.position || b?.attributes?.position
        )
      )
      .map(jobPosition => jobPosition.id)
      .join(','),
  ids => (ids.length ? ids.split(',') : [])
);

export const selectJobPositionIdsBySortWeek = createSelector(
  (state, { sortBy }) => {
    const jobPositionIds =
      sortBy === SORT_BY_WEEK_OPTIONS.POSITION
        ? selectJobPositionIds(state)
        : selectCustomJobPositionsIdsFromSlice(state);

    return jobPositionIds.join(',');
  },
  ids => (ids.length ? ids.split(',') : [])
);

const selectFilteredJobs = createSelector(
  getCurrentLocationIdForSchedule,
  (_, props) => props.sortBy,
  selectAllEmployeeIds,
  selectScheduledEmployeeIds,
  selectIsScheduleUsersOnly,
  selectUserIdUserNameMap,
  selectJobIdUserIdMap,
  selectSearchTerm,
  selectCustomJobIds,
  selectAllJobs,
  selectCurrentDateRangeEnd,
  (
    currentLocationId,
    sortBy,
    allEmployeeIds,
    scheduledEmployeeIds,
    isScheduleUsersOnly,
    userIdEmployeeNameMap,
    jobIdUserIdMap,
    searchTerm,
    customJobIds,
    jobs,
    currentDateRangeEnd
  ) => {
    let jobIds = isScheduleUsersOnly ? scheduledEmployeeIds : allEmployeeIds;

    const jobsInCurrentLocation = jobs
      .filter(
        job =>
          job?.location?.id === currentLocationId && jobIds.includes(job.id)
      )
      .map(j => j.id);

    if (sortBy === SORT_BY_WEEK_OPTIONS.POSITION) {
      jobIds = customJobIds.filter(jobId =>
        jobsInCurrentLocation.includes(jobId)
      );
    } else {
      jobIds = jobsInCurrentLocation.sort((a, b) => {
        const userA = getUser(userIdEmployeeNameMap, jobIdUserIdMap, a);
        const userB = getUser(userIdEmployeeNameMap, jobIdUserIdMap, b);

        const firstNameA = userA?.firstName?.toLowerCase() || '';
        const firstNameB = userB?.firstName?.toLowerCase() || '';

        const lastNameA = userA?.lastName?.toLowerCase() || '';
        const lastNameB = userB?.lastName?.toLowerCase() || '';

        if (sortBy === SORT_BY_WEEK_OPTIONS.FIRST_NAME) {
          return sortByAttribute(firstNameA, firstNameB, lastNameA, lastNameB);
        }

        if (sortBy === SORT_BY_WEEK_OPTIONS.LAST_NAME) {
          return sortByAttribute(lastNameA, lastNameB, firstNameA, firstNameB);
        }
        return 0;
      });
    }

    if (currentDateRangeEnd) {
      jobIds = jobIds.filter(jobId => {
        const job = jobs.find(j => j.id === jobId);
        if (!job?.hireDate) return true;
        return job.hireDate <= currentDateRangeEnd;
      });
    }

    if (!searchTerm) return jobIds;

    return jobIds.filter(employeeId => {
      const username =
        userIdEmployeeNameMap[jobIdUserIdMap[employeeId]]?.fullName;
      return new RegExp(searchTerm, 'gi').test(username);
    });
  }
);

export const selectFilteredJobIds = createSelector(
  (state, props) => selectFilteredJobs(state, props).join(','),
  ids => (ids.length ? ids.split(',') : [])
);

export const selectJobIdsSortedByStartTime = createSelector(
  selectJobIdUserIdMap,
  selectUserIdUserNameMap,
  state =>
    selectFilteredJobIds(state, {
      sortBy: VIEW_TYPES.startTime,
    }),
  selectScheduledEmployeeIds,
  selectIsScheduleUsersOnly,
  state => selectAllShifts(state),
  selectAllScheduleAvailabilities,
  selectAllTimeOffs,
  (state, props) => selectDatesInCurrentRange(state, props)[0],
  (
    jobIdUserIdMap,
    userIdUserNameMap,
    filterJobIds,
    scheduledEmployeeIds,
    isScheduleUsersOnly,
    shifts,
    scheduleAvailabilities,
    timeOffs,
    currentDate
  ) => {
    const jobIds = isScheduleUsersOnly ? scheduledEmployeeIds : filterJobIds;

    const sortedShiftJobIds = new Set(
      shifts
        .filter(
          shift =>
            getEntityCurrentDate(shift, currentDate) &&
            shift.owner.type === 'job' &&
            jobIds.includes(shift.owner.id)
        )
        .sort(shiftSortCompare)
        .map(shift => shift.owner.id)
    );

    const sortedTimeOffJobIds = timeOffs
      .filter(
        timeOff =>
          getEntityCurrentDate(timeOff, currentDate) &&
          jobIds.includes(timeOff.job?.id)
      )
      .sort(timeOffSortCompare)
      .map(timeOff => timeOff.job?.id);

    const sortedPreferredScheduleAvailabilitiesJobIds =
      getScheduleAvailabilitiesIds(
        scheduleAvailabilities,
        currentDate,
        'Preferred',
        jobIds
      );
    const sortedUnavailableScheduleAvailabilitiesJobIds =
      getScheduleAvailabilitiesIds(
        scheduleAvailabilities,
        currentDate,
        'Unavailable',
        jobIds
      );

    const itemIds = new Set([
      ...sortedShiftJobIds,
      ...sortedPreferredScheduleAvailabilitiesJobIds,
      ...sortedUnavailableScheduleAvailabilitiesJobIds,
      ...sortedTimeOffJobIds,
    ]);

    const nonItemIds = jobIds.filter(jobId => !itemIds.has(jobId));

    // eslint-disable-next-line array-callback-return
    const sortedNonItemIds = nonItemIds.sort((id1, id2) => {
      const userNameA = userIdUserNameMap[jobIdUserIdMap[id1]]?.fullName;
      const userNameB = userIdUserNameMap[jobIdUserIdMap[id2]]?.fullName;

      const nameA = userNameA.toLowerCase();
      const nameB = userNameB.toLowerCase();

      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
    });

    const sortedIds = new Set([
      ...sortedShiftJobIds,
      ...sortedPreferredScheduleAvailabilitiesJobIds,
      ...sortedNonItemIds,
      ...sortedUnavailableScheduleAvailabilitiesJobIds,
      ...sortedTimeOffJobIds,
    ]);

    return isScheduleUsersOnly
      ? Array.from(sortedShiftJobIds)
      : Array.from(sortedIds);
  }
);
/**
 * Selects job objects sorted by start time for the current date range NOT in the current location
 *
 * @param {Object} state - Redux state
 * @param {Object} props - Props containing current date
 * @returns {Array} Array of job objects
 */
const selectJobsNotInCurrentLocation = createSelector(
  getCurrentLocationIdForSchedule,
  selectAllJobs,
  selectDecoratedShifts,
  selectCurrentDateRange,
  (currentLocationId, jobs, shifts, dateRange) =>
    jobs?.filter(job => {
      const shiftsInDateRange = shifts.filter(shift =>
        isDateInRange(
          dateFromTime(shift.startAt),
          dateRange.start,
          dateRange.end
        )
      );

      const jobIdsInShifts = shiftsInDateRange
        .filter(s => s.owner.type === 'job')
        .map(s => s.owner.id);

      return (
        job.id &&
        job?.location?.id !== currentLocationId &&
        jobIdsInShifts.includes(job.id)
      );
    })
);

/**
 * Returns a mapping of job IDs to user IDs for jobs that are NOT in the current location
 * but have shifts in the current date range.
 * @example
 * // For jobs in other locations, maps job ID to user ID:
 * {
 *   'job123': 'user456',
 *   'job789': 'user012'
 * }
 * Used in conjunction with selectUserIdsInOtherLocations to identify users
 * who have shifts across multiple locations.
 */
export const selectJobIdUserIdMapNotCurrentLocation = createSelector(
  selectJobsNotInCurrentLocation,
  jobs =>
    jobs?.reduce((memo, job) => {
      if (job.user?.id) {
        memo[job.id] = job.user.id;
      }
      return memo;
    }, {})
);

export const selectJobIdLocationIdMapNotCurrentLocation = createSelector(
  selectJobsNotInCurrentLocation,
  jobs =>
    jobs?.reduce((memo, job) => {
      if (job?.location?.id) {
        memo[job.id] = job?.location?.id;
      }
      return memo;
    }, {})
);

/**
 * this returns an array of user ids that are in other locations
 * intersect with users in current location
 *
 * ['1', '2', '3']
 *
 * leaving this one here for now because it's hard
 * to test due to circular dependency imports
 */
export const selectUserIdsInOtherLocations = createSelector(
  selectJobIdUserIdMapNotCurrentLocation,
  selectJobIdUserIdMap,
  (jobIdUserIdMapNotInCurrentLocation, jobIdUserIdMap) =>
    intersection(
      Object.values(jobIdUserIdMap),
      Object.values(jobIdUserIdMapNotInCurrentLocation)
    )
);

/**
 * this returns job ids from other locations
 *
 * ['1', '2', '3']
 */
export const selectOtherJobLocationIds = createSelector(
  selectUserIdsInOtherLocations,
  selectJobIdLocationIdMapNotCurrentLocation,
  selectUserIdJobIdMap,
  (userIdsInOtherLocations, jobIdLocationIdMap, userIdJobIdMap) => {
    const jobIds = userIdsInOtherLocations
      .map(userId => userIdJobIdMap[userId])
      .flat();

    const locationIds = uniq(
      jobIds
        .map(jobId => jobIdLocationIdMap[jobId])
        .filter(locationId => locationId)
    );
    return locationIds;
  }
);

const selectNumShiftsByDate = createSelector(
  selectShiftsByDateAndJob,
  shiftsByDateAndJob =>
    Object.entries(shiftsByDateAndJob).reduce((memo, [date, shiftsByJob]) => {
      memo[date] = Object.keys(shiftsByJob).length;
      return memo;
    }, {})
);

export const selectShiftIdsByDate = createCachedSelector(
  (state, { date }) =>
    selectAllShifts(state)
      .filter(
        shift =>
          dateFromTime(shift.startAt) === date &&
          containsShiftsInCurrentLocation(
            shift,
            selectJobIdsForCurrentLocation(state)
          )
      )
      .sort(shiftSortCompare)
      .map(shift => shift.id)
      .join(','),
  ids => (ids.length ? ids.split(',') : [])
)((_, props) => `selectShiftIdsByDate-${props.date}`);

export const selectNumEmployeesScheduledOnDate = createCachedSelector(
  selectNumShiftsByDate,
  (state, props) => props.date,
  (numShiftsByDate, date) => numShiftsByDate[date] || 0
)((_, props) => `selectNumEmployeesScheduledOnDate-${props.date}`);

const selectDeletedShiftsCount = state =>
  selectSliceAttribute({
    state,
    attribute: 'deletedShiftsCount',
    sliceName: 'schedule',
  }) || 0;

const selectUnpublishedCountInCurrentDateRangeAndLocation = createSelector(
  getCurrentLocationIdForSchedule,
  selectShiftIdJobIdMap,
  selectJobIdLocationIdMap,
  selectDecoratedShifts,
  selectCurrentDateRange,
  (currentLocationId, shiftIdJobIdMap, jobIdLocationIdMap, shifts, dateRange) =>
    shifts.filter(shift => {
      const ownerId = shiftIdJobIdMap[shift.id];

      const openShiftCurrentLocationId =
        shift.owner.type === 'location' && ownerId;

      const isOpenShiftInCurrentLocation =
        openShiftCurrentLocationId === currentLocationId;

      const shiftLocationId = jobIdLocationIdMap[ownerId];

      return (
        isDateInRange(shift.decoration.date, dateRange.start, dateRange.end) &&
        !shift.isPublished &&
        (shiftLocationId === currentLocationId || isOpenShiftInCurrentLocation)
      );
    })?.length || 0
);

export const selectUnpublishedShiftsCount = createSelector(
  selectUnpublishedCountInCurrentDateRangeAndLocation,
  selectDeletedShiftsCount,
  (unpublishedCount, deletedCount) => unpublishedCount + deletedCount
);

export const selectHasUnpublishedShifts = createSelector(
  selectDeletedShiftsCount,
  selectUnpublishedCountInCurrentDateRangeAndLocation,
  (deletedCount, unpublishedCount) => !!(deletedCount + unpublishedCount)
);

export const selectHasPublishedShifts = createSelector(
  selectDeletedShiftsCount,
  state =>
    selectShiftWhereAttribute(state, { attribute: 'isPublished', value: true })
      ?.length || 0,
  (deletedCount, publishedCount) => !!(deletedCount + publishedCount)
);

export const selectHasPublishedShiftsInCurrentDateRangeAndLocation =
  createSelector(
    getCurrentLocationIdForSchedule,
    selectShiftIdJobIdMap,
    selectJobIdLocationIdMap,
    selectDecoratedShifts,
    selectCurrentDateRange,
    (
      currentLocationId,
      shiftIdJobIdMap,
      jobIdLocationIdMap,
      shifts,
      dateRange
    ) =>
      shifts.filter(shift => {
        const ownerId = shiftIdJobIdMap[shift.id];

        const openShiftCurrentLocationId =
          shift.owner.type === 'location' && ownerId;

        const isOpenShiftInCurrentLocation =
          openShiftCurrentLocationId === currentLocationId;

        const shiftLocationId = jobIdLocationIdMap[ownerId];

        return (
          isDateInRange(
            shift.decoration.date,
            dateRange.start,
            dateRange.end
          ) &&
          shift.isPublished &&
          (shiftLocationId === currentLocationId ||
            isOpenShiftInCurrentLocation)
        );
      })?.length || 0
  );

export const selectScheduleHasShiftsInCurrentDateRange = createSelector(
  selectHasPublishedShifts,
  selectHasUnpublishedShifts,
  (hasPublishedShifts, hasUnpublishedShifts) =>
    hasPublishedShifts || hasUnpublishedShifts
);

export const selectScheduleHasShiftsInCurrentDateRangeAndLocation =
  createSelector(
    selectHasPublishedShiftsInCurrentDateRangeAndLocation,
    selectHasUnpublishedShifts,
    (hasPublishedShifts, hasUnpublishedShifts) =>
      hasPublishedShifts || hasUnpublishedShifts
  );

export const selectShouldAllowResendNotifications = createSelector(
  selectHasPublishedShifts,
  selectHasUnpublishedShifts,
  (hasPublishedShifts, hasUnpublishedShifts) =>
    hasPublishedShifts && !hasUnpublishedShifts
);

export const selectShiftsWithConflicts = createSelector(
  getCurrentLocationIdForSchedule,
  selectCurrentDateRange,
  selectShiftIdJobIdMap,
  selectJobIdLocationIdMap,
  state =>
    uniq(
      selectAllConflicts(state).reduce((memo, conflict) => {
        if (conflict.sourceId && conflict.sourceType === 'shift') {
          memo.push(conflict.sourceId);
        }
        return memo;
      }, [])
    ).map(id => selectShiftById(state, id)),
  (currentLocationId, dateRange, shiftIdJobIdMap, jobIdLocationIdMap, shifts) =>
    shifts.filter(shift => {
      const ownerId = shiftIdJobIdMap[shift.id];

      const shiftLocationId = jobIdLocationIdMap[ownerId];

      return (
        isShiftWithinRange(shift, dateRange.start, dateRange.end) &&
        shiftLocationId === currentLocationId
      );
    })
);

export const selectHasConflicts = createSelector(
  selectShiftsWithConflicts,
  shiftsWithConflicts => !!shiftsWithConflicts?.length
);

export const selectEmployeesInRoleDepartment = createSelector(
  (state, { roleId }) =>
    (!roleId || roleId === 'uncategorized'
      ? selectJobsWhereAttribute(state, {
          attribute: 'defaultRoleId',
          value: null,
        })
      : selectAllDepartments(state)
          .find(dept =>
            /** Find department with the roleId */
            dept.roles.some(role => role.id === roleId)
          ) /**  Get all the roles in the department found above */
          ?.roles.map(role => role.id)
          .reduce(
            /**  Get all jobs with the roles found above */
            (memo, id) => [
              ...memo,
              ...selectJobsWhereAttribute(state, {
                attribute: 'defaultRoleId',
                // Parsed to int because job.defaultRoleId is an integer on the API side
                value: parseInt(id, 10),
              }),
            ],
            []
          )
    ).map(j => j.id),
  employeesInRoleDepartment => employeesInRoleDepartment
);

export const selectEmployeesNotInRoleDepartment = createSelector(
  selectEmployeesInRoleDepartment,
  state => selectAllJobs(state).map(j => j.id),
  (employeesInRoleDepartment, allJobs) =>
    allJobs.filter(jobId => !employeesInRoleDepartment.includes(jobId))
);

export const sortEmployeesInRoleDepartment = createSelector(
  selectEmployeesInRoleDepartment,
  selectJobIdUserIdMap,
  selectUserIdUserNameMap,
  (employeesInRoleDepartment, jobIdUserIdMap, userIdUserNameMap) =>
    employeesInRoleDepartment.sort(
      sortEmployeeInDepartmentByName(jobIdUserIdMap, userIdUserNameMap)
    )
);

export const sortEmployeesNotInRoleDepartment = createSelector(
  selectEmployeesNotInRoleDepartment,
  selectJobIdUserIdMap,
  selectUserIdUserNameMap,
  (employeesNotInRoleDepartment, jobIdUserIdMap, userIdUserNameMap) =>
    employeesNotInRoleDepartment.sort(
      sortEmployeeInDepartmentByName(jobIdUserIdMap, userIdUserNameMap)
    )
);

export const selectRoleEmployeeOptions = createSelector(
  sortEmployeesInRoleDepartment,
  sortEmployeesNotInRoleDepartment,
  selectJobIdUserIdMap,
  selectUserIdUserNameMap,
  (
    employeesInRoleDepartment,
    employeesNotInRoleDepartment,
    jobIdUserIdMap,
    userIdUserNameMap
  ) =>
    [...employeesInRoleDepartment, ...employeesNotInRoleDepartment].reduce(
      (memo, jobId, index) => {
        if (
          (!index && employeesInRoleDepartment.length) ||
          index === employeesInRoleDepartment.length
        )
          memo.push({
            label: !index
              ? toI18n(`${SHIFT_FORM_I18N_PATH}.employees_in_department`)
              : toI18n(`${SHIFT_FORM_I18N_PATH}.all_other_employees`),
            disabled: true,
          });

        const userId = jobIdUserIdMap[jobId];

        if (userIdUserNameMap[userId])
          memo.push({
            label: userIdUserNameMap[userId]?.fullName,
            value: jobId,
          });

        return memo;
      },
      []
    )
);

export const selectEmployeeOptions = createSelector(
  selectJobIdUserIdMap,
  selectJobIdArchivedAtMap,
  (_, props) => props.currentShiftDate,
  selectUserIdUserNameMap,
  (jobIdUserIdMap, jobIdArchivedAtMap, shiftDate, userIdUserNameMap) => {
    Object.entries(jobIdArchivedAtMap).forEach(([jobId, archiveDate]) => {
      const jobArchivedBeforeShiftDate =
        compareDateTime(archiveDate, shiftDate) <= 0;

      if (shiftDate && jobArchivedBeforeShiftDate) {
        delete jobIdUserIdMap[jobId];
      }
    });

    return Object.entries(jobIdUserIdMap)
      .map(([jobId, userId]) => ({
        label: userIdUserNameMap[userId]?.fullName,
        value: jobId,
      }))
      .sort((a, b) => a?.label?.localeCompare(b?.label) || 0);
  }
);

export const selectShouldShowConsecutiveDayWarning = createCachedSelector(
  selectShiftsByJobAndDate,
  selectDatesInCurrentRange,
  (_, props) => props.jobId,
  (shiftsByJobAndDate, dateRange, jobId) => {
    const shifts = [];
    const jobShiftsByDate = shiftsByJobAndDate[jobId] || {};

    if (!jobShiftsByDate) return false;

    dateRange.forEach(date => {
      if (jobShiftsByDate[date]) {
        shifts.push(Object.keys(jobShiftsByDate[date]));
      }
    });

    const numJobShifts = Object.keys(shifts).length;

    if (!jobShiftsByDate || numJobShifts <= CONSECUTIVE_SHIFTS_MAX)
      return false;

    return (
      numJobShifts === 7 ||
      (numJobShifts > CONSECUTIVE_SHIFTS_MAX &&
        (!jobShiftsByDate[dateRange[0]] || !jobShiftsByDate[dateRange[6]]))
    );
  }
)((_, props) => `selectShouldShowConsecutiveDayWarning-${props.jobId}`);

export const selectUncategorizedShifts = createSelector(
  state => selectAllShifts(state),
  shifts => shifts.filter(shift => !shift?.role?.id)
);

export const selectShiftIdsByRoleAndDate = createCachedSelector(
  (state, { roleId, date }) => {
    const shifts =
      roleId === 'uncategorized'
        ? selectUncategorizedShifts(state)
        : selectShiftsByRoleId(state, roleId);

    return shifts
      .sort(shiftSortCompare)
      .filter(
        shift =>
          dateFromTime(shift.startAt) === date &&
          containsShiftsInCurrentLocation(
            shift,
            selectJobIdsForCurrentLocation(state)
          )
      )
      .map(shift => shift.id)
      .join(',');
  },
  ids => (ids.length ? ids.split(',') : [])
)((_, { roleId, date }) => `selectShiftIdsByRoleAndDate-${roleId}-${date}`);

export const selectOpenTime = state =>
  selectLocationAttribute({
    state,
    attribute: 'openTime',
    id: getCurrentLocationIdForSchedule(state),
  });

export const selectClosingTime = state =>
  selectLocationAttribute({
    state,
    attribute: 'closingTime',
    id: getCurrentLocationIdForSchedule(state),
  });

export const selectEnabledWeeklyOvertime = state =>
  selectLocationAttribute({
    state,
    attribute: 'enabledWeeklyOvertime',
    id: getCurrentLocationIdForSchedule(state),
  });

export const selectWeeklyOvertime = state =>
  selectLocationAttribute({
    state,
    attribute: 'weeklyOvertime',
    id: getCurrentLocationIdForSchedule(state),
  });

export const selectEnabledDailyOvertime = state =>
  selectLocationAttribute({
    state,
    attribute: 'enabledDailyOvertime',
    id: getCurrentLocationIdForSchedule(state),
  });

export const selectDailyOvertime = state =>
  selectLocationAttribute({
    state,
    attribute: 'dailyOvertime',
    id: getCurrentLocationIdForSchedule(state),
  });

export const selectSendToPartnerSyncErrors = createSelector(
  selectSlice,
  slice => slice.syncErrors
);

export const selectSendToPartnerDrawerShown = createSelector(
  selectSlice,
  slice => slice.activeDrawer === DRAWERS.send_to_partner_drawer
);

export const selectSyncShiftStatuses = createSelector(
  selectSlice,
  slice => slice.syncShiftStatus
);

/* convert these TS types when this file is refractored to TS */
export const selectSyncShiftError = createSelector(
  [selectSlice, (_, shiftId) => shiftId],
  (slice, shiftId) => {
    if (!shiftId) return undefined;

    const errorKey = slice.syncShiftStatus?.find(
      shift => shift.id.toString() === shiftId.toString()
    )?.sync_error;

    if (!errorKey) return undefined;

    const validErrors = [
      'missed_job_id',
      'missed_employee_role',
      'wrong_job_partner_id',
      'wrong_role_partner_id',
      'duplicate_shift_id',
      'other',
    ];

    return validErrors.includes(errorKey) ? errorKey : 'other';
  }
);

export const selectPartnerSyncState = createSelector(
  selectSlice,
  slice => slice.partnerSyncState
);

export const selectActiveModal = createSelector(
  selectSlice,
  slice => slice.activeModal
);

export const selectIsPredictiveSchedulingModalActive = createSelector(
  selectSlice,
  slice => slice.activeModal === MODALS.predictive_scheduling_generation_modal
);

export const selectIsPredictiveGenerationFeedbackDialogOpen = createSelector(
  selectSlice,
  slice => slice.activeDialog === DIALOGS.predictive_scheduling_feedback_dialog
);

export const selectSchedulePredictionGeneration = createSelector(
  selectSlice,
  slice => slice.schedulePredictionGeneration
);

export const selectPredictedShiftsCount = createSelector(
  selectSlice,
  slice => slice.predictedShiftsCount
);

export const selectPredictionBaseStartDate = createSelector(
  selectSlice,
  slice => slice.predictionBaseStartDate
);

export const selectPredictionBaseEndDate = createSelector(
  selectSlice,
  slice => slice.predictionBaseEndDate
);

export const selectPublishLoading = createSelector(
  selectSlice,
  slice => slice.publishLoading
);

export const selectNarrativeMessages = createSelector(
  selectSlice,
  slice => slice.narrativeMessages
);

export const selectManualBaseStartDate = createSelector(
  selectSlice,
  slice => slice.predictionManualBaseStartDate
);

export const selectManualBaseEndDate = createSelector(
  selectSlice,
  slice => slice.predictionManualBaseEndDate
);

export const selectPredictionError = createSelector(
  selectSlice,
  slice => slice.predictionError
);

export const selectPublishedCount = createSelector(
  selectSlice,
  slice => slice.publishedCount
);
